import { ButtonProps } from '@affinis/smartus-components/lib/button/button.types';
import { Chip } from '@affinis/smartus-components/lib/chips/chips.component.types';
import { MenuItem } from '@affinis/smartus-components/lib/menu-extra/menu-extra.component';
import {
  Component,
  Injector,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  BaseSearchOptions,
  BaseSelectViewModel,
  IncidentClient,
  IncidentFilterOptions,
  IncidentSearchOptions,
  IncidentTableEntryTaskViewModel,
  IncidentTableEntryViewModel,
  IncidentViews,
  OmContact,
  OptionSetClient,
  ResultWrapperOfDtagIncidentTableEntryViewModel,
  SysEnum,
} from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { OplExtService } from '@core/services/opl-ext-service/opl-ext.service';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { IbpdiHelperService } from '@core/services/translate-service/ibpdi-helper.service';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ModalFilterComponent } from '@shared/components/modal-filter/modal-filter.component';
import { TableBaseComponent } from '@shared/components/table-base/table-base';
import { ITableConfig } from '@shared/components/table-base/table-base.types';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { TaskSubject } from '@shared/constants/taskSubjects.constants';
import { excelExport } from '@shared/utils/excel-export';
import moment from 'moment';
import {
  Observable,
  Subscription,
  catchError,
  finalize,
  first,
  of,
  switchMap,
  tap,
} from 'rxjs';

interface ITicketOption {
  label: string;
  value: string;
}

const filterOptionsDtagKey = 'filterOptionsTicketsDtag';
const ticketTaskSelectionKey = 'ticketTaskSelection';
const ticketViewSelectionKey = 'ticketViewSelection';

@Component({
  selector: 'app-ticket-list-dtag',
  templateUrl: './ticket-list-dtag.component.html',
  styleUrls: ['./ticket-list-dtag.component.scss'],
  standalone: false,
})
export class TicketListDtagComponent
  extends TableBaseComponent
  implements OnInit
{
  override config = this.configure(<ITableConfig>{
    title: 'taskmodule.tickets.list.title',
    translatePath: 'taskmodule.shared',
  });

  form = new FormGroup({});
  user: OmContact;

  @ViewChild('ticketActionsTemplate', { static: true })
  ticketActionsTemplateRef!: TemplateRef<void>;

  controlButtons: ButtonProps[] = [
    {
      label: this.translateService.instant('crud.refresh'),
      onClick: () => this.refresh(),
    },
  ];

  private optionSetSubscription: Subscription;

  private incidentClassSubscription: Subscription;

  organisationName: string;

  organisations: string[] = [
    'ISS Vermietung',
    'ISS Property Management',
    'ISS An-/Abmietung',
    'ISS Capital Projects',
    'ISS Vertragsmanagement',
  ];

  canDownload: boolean = false;

  chips: Chip[] = [];

  menuItems: MenuItem[] = [];

  override columns: MtxGridColumn[] = [];

  override tableIdentifier = 'ticketListDtag';

  ticketTaskControl = new FormControl(
    this.sessionStorageService.get(ticketTaskSelectionKey)
      .ticketTaskSelection ?? 'MyDtagIncidents'
  );

  ticketViewControl = new FormControl(
    this.sessionStorageService.get(ticketViewSelectionKey)
      .ticketViewSelection ?? UserRoles.SMARTPORTAL_DTAG_USER
  );

  ticketTaskOptions: ITicketOption[] = [
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.my_active_tickets'
      ),
      value: 'MyDtagIncidentsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.active_tickets'
      ),
      value: 'AllDtagIncidentsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.my_tickets_all'
      ),
      value: 'MyDtagIncidents',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.all_tickets'
      ),
      value: 'All',
    },
  ];

  originalTicketTaskOptions = [...this.ticketTaskOptions];

  userRelatedTicketTaskOptions: ITicketOption[] = [
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.my_active_tickets'
      ),
      value: 'MyDtagIncidentsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.my_tickets_all'
      ),
      value: 'MyDtagIncidents',
    },
  ];

  restrictedTicketTaskOptions: ITicketOption[] = [
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.active_tickets'
      ),
      value: 'AllDtagIncidentsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.all_tickets'
      ),
      value: 'All',
    },
  ];

  oplExtTicketTaskOptions: ITicketOption[] = [
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.my_tasks'
      ),
      value: 'MyActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.active_tickets'
      ),
      value: 'AllDtagIncidentsWithActiveTasks',
    },
    {
      label: this.translateService.instant(
        'taskmodule.tickets.list.ticket_task_selection.all_tickets'
      ),
      value: 'All',
    },
  ];

  ticketViewOptions: BaseSelectViewModel[] = [];

  override filterFields: Array<FormlyFieldConfig> = this.getFilterFields();

  modalComponent = ModalFilterComponent;

  loading = false;

  ticketViewChanged = true;

  dialogData;

  isOPLExt = false;

  TaskSubject = TaskSubject;

  constructor(
    injector: Injector,
    private readonly ibpdiHelperService: IbpdiHelperService,
    private readonly incidentClient: IncidentClient,
    private readonly optionSetClient: OptionSetClient,
    private readonly breadcrumbsService: BreadcrumbService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly locationFormlyFieldService: LocationFormlyFieldService,
    private readonly oplExtService: OplExtService
  ) {
    super(injector);
  }

  public ngOnInit() {
    this.oplExtService
      .checkIsOplExt()
      .pipe(
        tap(hasRole => {
          if (hasRole) {
            this.isOPLExt = true;
          }
        })
      )
      .subscribe();

    this.filterFields = this.getFilterFields();

    this.authClient.getDtagRoleSelectionForCurrentUser().subscribe({
      next: (res: BaseSelectViewModel[]) => {
        this.hasOnlyDtagUserRole().subscribe(hasOnlyDtagRole => {
          if (hasOnlyDtagRole) {
            this.ticketViewOptions = res.filter(
              x => x.id === UserRoles.SMARTPORTAL_DTAG_USER
            );
            this.ticketViewControl.setValue(UserRoles.SMARTPORTAL_DTAG_USER);
          } else {
            this.ticketViewOptions = [...res];
          }

          // Prüfen ob der aktuell eingestellte Wert in res vorhanden ist
          const currentValue = this.ticketViewControl.value;
          const valueExists = res.some(item => item.id === currentValue);

          if (!valueExists) {
            // Prüfen ob UserRoles.SMARTPORTAL_DTAG_USER vorhanden ist
            const hasDtagUserRole = res.some(
              item => item.id === UserRoles.SMARTPORTAL_DTAG_USER
            );

            if (hasDtagUserRole) {
              this.ticketViewControl.setValue(UserRoles.SMARTPORTAL_DTAG_USER);
            } else {
              this.ticketViewControl.setValue(null);
            }
          }

          if (!this.ticketViewOptions.includes(this.ticketViewControl.value)) {
            this.sessionStorageService.set(ticketViewSelectionKey, {
              ticketViewSelection: this.ticketViewControl.value,
            });
          }

          this.handleTicketTaskOptions();

          const currentTaskValue = this.ticketTaskControl.value;
          const isTaskValueValid = this.ticketTaskOptions.some(
            option => option.value === currentTaskValue
          );

          if (!isTaskValueValid && this.ticketTaskOptions.length > 0) {
            this.ticketTaskControl.setValue(this.ticketTaskOptions[0].value);
            this.sessionStorageService.set(ticketTaskSelectionKey, {
              ticketTaskSelection: this.ticketTaskOptions[0].value,
            });
          }
        });
      },
      error: err => {
        console.error(
          'Error fetching role selection or ticket view options:',
          err
        );
      },
    });

    this.breadcrumbsService.setBreadcrumbs([
      {
        label: this.translateService.instant('taskmodule.tickets.list.title'),
      },
    ]);

    this.columns = [
      {
        field: 'showExpand',
        showExpand: true,
        sortable: false,
        resizable: false,
        width: '90px',
        header: '',
        class: 'expansion-header',
      },
      {
        header: this.translate('label__ticketNumber'),
        field: 'ticketNumber',
        sortable: true,
        resizable: true,
        minWidth: 180,
      },
      {
        header: this.translate('label__incidentTitle'),
        field: 'title',
        sortable: true,
        class: 'truncated',
        resizable: true,
        width: '200dp',
        minWidth: 200,
      },
      {
        header: this.translate('label__incidentDescription'),
        field: 'description',
        sortable: true,
        class: 'truncated',
        resizable: true,
        minWidth: 195,
        hide: true,
      },
      {
        header: this.translate('label__incidentClass'),
        field: 'ticketClass',
        sortable: true,
        class: '',
        resizable: true,
      },
      {
        header: this.translate('label__product'),
        field: 'product',
        sortable: true,
        class: '',
        resizable: true,
        hide: true,
      },
      {
        header: this.translate('label__economicUnit'),
        field: 'economicUnit',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__postalCode'),
        field: 'postalCode',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__incidentCity'),
        field: 'city',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__incidentStreet'),
        field: 'street',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__statusReason'),
        field: 'status',
        sortable: true,
        class: 'truncated',
        resizable: true,
      },
      {
        header: this.translate('label__rejectionReason'),
        field: 'rejection',
        sortable: true,
        class: 'truncated',
        resizable: true,
        hide: true,
      },
      {
        header: this.translate('label__createdOn'),
        field: 'createdOn',
        sortable: true,
        class: 'truncated',
        formatter: (data: any) =>
          data.createdOn
            ? moment(data.createdOn)
                .locale(this.translateService.instant('locale.locale'))
                .format(
                  this.translateService.instant(
                    'locale.moment__date_time_format'
                  )
                ) +
              ' ' +
              this.translate('extended_time_str')
            : '--',
        resizable: true,
        width: '140px',
        minWidth: 134,
      },
      {
        header: this.translate('label__documents'),
        width: '120px',
        field: 'action',
        cellTemplate: this.ticketActionsTemplateRef,
        resizable: true,
      },
    ];
    this.loadColumnVisibilityAndOrder();
    this.userClient.getUserProfile().subscribe({
      next: profile => {
        this.user = profile;
      },
      error: err => {
        if (err.errorCode === 5002) {
          let count = 0;
          this.userService.isGsusUserBehavior$.subscribe({
            next: isGsus => {
              this.sysUser = isGsus;
              if (this.sysUser) {
                this.ticketTaskOptions = [
                  {
                    label: this.translateService.instant(
                      'taskmodule.tickets.list.ticket_task_selection.active_tickets'
                    ),
                    value: 'AllDtagIncidentsWithActiveTasks',
                  },
                  {
                    label: this.translateService.instant(
                      'taskmodule.tickets.list.ticket_task_selection.all_tickets'
                    ),
                    value: 'All',
                  },
                ];
                if (
                  !this.filterFields.some(
                    field => field.key === 'organisation'
                  ) &&
                  count === 0
                ) {
                  count = 1;
                  this.filterFields[0].fieldGroup.push({
                    type: 'select',
                    key: 'organisation',
                    props: {
                      label: 'Firma',
                      options: this.organisations.map(organisation => ({
                        label: organisation,
                        value: organisation,
                      })),
                    },
                  });
                }
              }
            },
          });
        }
      },
    });

    let filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);
    this.config.grid.filter.filterOptions = { ...filterOptions };

    this.config.grid.filter.filterOptions.economicUnit =
      this.config.grid.filter.filterOptions.economicUnit?.id;

    this.hasOnlyDtagUserRole().subscribe(hasDtagRole => {
      if (hasDtagRole) this.sessionStorageService.clear();

      filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);
      this.config.grid.filter.filterOptions = { ...filterOptions };

      this.config.grid.filter.filterOptions.economicUnit =
        this.config.grid.filter.filterOptions.economicUnit?.id;

      this.mapFormModelToChips(filterOptions);

      this.updateFilter({
        ...filterOptions,
        ticketClassList: filterOptions.ticketClassList
          ? filterOptions.ticketClassList.map(x => x.id)
          : null,
        economicUnit: filterOptions.economicUnit?.id,
      });

      this.refreshFromFilter();
    });

    this.ticketViewControl.valueChanges.subscribe({
      next: () => {
        this.ticketViewChanged = true;
      },
    });

    this.dialogData = {
      columns: this.columns,
      filterFields: this.filterFields,
      filterValues: this.sessionStorageService.get(filterOptionsDtagKey),
      actions: {
        onSubmit: (formModel: any) => {
          this.sessionStorageService.set(filterOptionsDtagKey, formModel);

          filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips(formModel);
          this.updateFilter({
            ...formModel,
            ticketClassList: formModel.ticketClassList
              ? formModel.ticketClassList.map(x => x.id)
              : null,
            economicUnit:
              filterOptions.economicUnit?.id ||
              filterOptions.economicUnit ||
              null,
          });

          this.refreshFromFilter();
          this.dialogData.filterValues =
            this.sessionStorageService.get(filterOptionsDtagKey);
          this.organisationName =
            this.config.grid.filter.filterOptions.organisation;
        },
        onReset: () => {
          this.sessionStorageService.set(filterOptionsDtagKey, {});
          this.resetLocationSelection();

          filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);
          this.config.grid.filter.filterOptions = { ...filterOptions };

          this.mapFormModelToChips({});

          this.refreshFromFilter();
        },
      },
    };
  }

  hasOnlyDtagUserRole(): Observable<boolean> {
    const userRoles = this.userService.getCurrentUserRoles();
    const dtagRoleCount = userRoles.filter(role =>
      role.includes(UserRoles.SMARTPORTAL_DTAG_BASE)
    ).length;

    if (dtagRoleCount !== 1) return of(false);

    return this.userService.checkIfUserHasRole(UserRoles.SMARTPORTAL_DTAG_USER);
  }

  override getTableData$ = (
    query?: BaseSearchOptions,
    filterOptions?: IncidentFilterOptions
  ) => {
    (this.form.value as any).ticketTaskSelection = this.ticketTaskControl.value;
    let view: IncidentViews = IncidentViews.MyDtagIncidents;

    if ((this.form.value as any).ticketTaskSelection === 'All') {
      view = IncidentViews.AllIncidents;
    } else if (
      (this.form.value as any).ticketTaskSelection ===
      'MyDtagIncidentsWithActiveTasks'
    ) {
      view = IncidentViews.MyDtagIncidentsWithActiveTasks;
    } else if (
      (this.form.value as any).ticketTaskSelection ===
      'AllDtagIncidentsWithActiveTasks'
    ) {
      view = IncidentViews.AllDtagIncidentsWithActiveTasks;
    }

    const searchOptions: IncidentSearchOptions = {
      ...query,
      filterOptions,
      viewOptions: {
        selectedView: view,
        selectedRoles:
          this.ticketViewControl?.value &&
          this.ticketViewControl?.value !== 'all'
            ? [this.ticketViewControl.value]
            : null,
      },
    };

    this.closeToast();

    return this.incidentClient.getDtagTickets(searchOptions).pipe(
      tap(res => {
        if (res && res.entites?.length > 0) {
          this.canDownload = true;
        }
      }),
      catchError(() => {
        this.canDownload = false;

        this.activeToast = this.toastr.error(
          this.translateService.instant(
            'crud.details.error.failed_to_load_msg'
          ),
          this.translateService.instant(
            'crud.details.error.failed_to_load_title'
          ),
          { timeOut: 0 }
        );

        return of([]);
      })
    );
  };

  override clickRow = (data, router: Router) => {
    // Prüfen ob DTAG_OP_EK View aktiv ist
    if (this.ticketViewControl.value === UserRoles.SMARTPORTAL_DTAG_OP_EK) {
      const tasks = data.rowData.tasks as IncidentTableEntryTaskViewModel[];

      // Suche nach Aufgabe 2.1 oder 2.2 die "in Bearbeitung" oder "Nicht gestartet" ist
      const task21or22InProgress = tasks?.find(
        task =>
          task.isActive &&
          task.subject ===
            TaskSubject.OPL_EXT_EVALUATE_OFFERS_AND_CREATE_SUPPLIERS &&
          (task.status === 'In Bearbeitung' ||
            task.status === 'Nicht gestartet')
      );

      if (task21or22InProgress) {
        // Aufgabe 2.1 oder 2.2 in Bearbeitung/Nicht gestartet → task/details
        router.navigate(
          [RoutePath.TASK.replace(':id', task21or22InProgress.taskId)],
          {
            state: { originRoute: 'tickets_dtag' },
          }
        );
        return;
      }

      // Suche nach Aufgabe 2.1 oder 2.2 die aktiv und abgeschlossen ist
      const task21or22Completed = tasks?.find(
        task =>
          task.isActive &&
          task.subject ===
            TaskSubject.OPL_EXT_EVALUATE_OFFERS_AND_CREATE_SUPPLIERS &&
          task.status === 'Abgeschlossen'
      );

      if (task21or22Completed) {
        // Aufgabe 2.1/2.2 abgeschlossen → task/viewDetails
        router.navigate([
          RoutePath.TASK_VIEW.replace(':id', task21or22Completed.taskId),
        ]);
        return;
      }

      // Suche nach Aufgabe 2.1 oder 2.2 die aktiv und storniert ist
      const task21or22Cancelled = tasks?.find(
        task =>
          task.isActive &&
          task.subject ===
            TaskSubject.OPL_EXT_EVALUATE_OFFERS_AND_CREATE_SUPPLIERS &&
          task.status === 'Storniert'
      );

      if (task21or22Cancelled) {
        // Aufgabe 2.1/2.2 storniert → task/viewDetails
        router.navigate([
          RoutePath.TASK_VIEW.replace(':id', task21or22Cancelled.taskId),
        ]);
        return;
      }

      // Fallback: In die erste Aufgabe springen
      const firstTask = tasks?.[0];
      if (firstTask) {
        router.navigate([RoutePath.TASK_VIEW.replace(':id', firstTask.taskId)]);
        return;
      }
    }

    // Standard-Verhalten für andere Views
    router.navigateByUrl('/helpdesk/my-tickets/' + data.rowData.id, {
      state: {
        incidentClassName: data.rowData.ticketClass,
        origin: 'ticketListDtag',
      },
    });
  };

  clickExpandedRow(row: IncidentTableEntryTaskViewModel) {
    if (!row.isActive && row.organisationId === this.user.organisationId) {
      this.router.navigate(['/task/viewDetails', row.taskId]);
      return;
    }

    if (!row.isActive && row.organisationId !== this.user.organisationId)
      return;

    if (
      row.status === 'Abgeschlossen' ||
      (row.status === 'Storniert' &&
        row.organisationId === this.user.organisationId)
    ) {
      this.router.navigate([RoutePath.TASK_VIEW.replace(':id', row.taskId)]);
    } else {
      const navigationExtras = this.isOPLExt
        ? { state: { originRoute: 'tickets_dtag' } }
        : {};
      this.router.navigate(
        [RoutePath.TASK.replace(':id', row.taskId)],
        navigationExtras
      );
    }
  }

  clickExpandedRowGSUS(row: IncidentTableEntryTaskViewModel) {
    this.router.navigate(['/task/viewDetails', row.taskId]);
  }

  openDocuments(event: MouseEvent, ticket: IncidentTableEntryViewModel) {
    event.stopPropagation();
    this.router.navigate(
      [`${RoutePath.DOCUMENTS}/ticket/${ticket.ticketNumber}`],
      {
        state: {
          origin: 'ticketListDtag',
        },
      }
    );
    return false;
  }

  openContact(event: MouseEvent, row: IncidentTableEntryTaskViewModel): void {
    event.stopPropagation();
    const taskId = row.taskId;

    const url = `/task/details/${taskId}`;
    const navigationExtras: any = { queryParams: { tab: 'contact' } };
    if (this.isOPLExt) {
      navigationExtras.state = { originRoute: 'tickets_dtag' };
    }
    this.router.navigate([url], navigationExtras).catch(err => {
      console.error('Navigation error:', err);
    });
  }

  openContactDetails(
    event: MouseEvent,
    row: IncidentTableEntryTaskViewModel
  ): void {
    event.stopPropagation();
    const taskId = row.taskId;

    const url = `/task/viewDetails/${taskId}`;
    this.router
      .navigate([url], { queryParams: { tab: 'contact' } })
      .catch(err => {
        console.error('Navigation error:', err);
      });
  }

  checkOrganisation(task: IncidentTableEntryTaskViewModel) {
    if (this.config.grid.filter.filterOptions.organisation) {
      return (
        this.config.grid.filter.filterOptions.organisation ===
        task.organisationName
      );
    }
    return this.organisations.includes(task.organisationName);
  }

  onCloseDialog = (e: any) => {
    this.resetLocationSelection();
  };

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.grid.filter.filterOptions[eventObj.id];

    this.sessionStorageService.set(filterOptionsDtagKey, {
      ...this.config.grid.filter.filterOptions,
      ticketClassList: this.config.grid.filter.filterOptions.ticketClassList
        ? this.sessionStorageService.get(filterOptionsDtagKey).ticketClassList
        : null,
    });

    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsDtagKey);

    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.refreshFromFilter();
  };

  mapFormModelToChips = formModel => {
    this.chips = [];
    this.chips.length = 0;

    Object.keys(formModel).forEach(formModelItemKey => {
      const flattened = this.filterFields[0].fieldGroup?.flatMap(field =>
        field.fieldGroup ? [field, ...field.fieldGroup] : field
      );
      const field = flattened?.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });
      if (field) {
        if (
          formModelItemKey === 'createDateTo' ||
          formModelItemKey === 'createDateFrom'
        ) {
          const date = new Date(formModel[formModelItemKey]).toLocaleDateString(
            'de-DE'
          );
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${date}`,
          });
        } else if (
          formModelItemKey === 'ticketClassList' &&
          formModel[formModelItemKey]
        ) {
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${formModel[formModelItemKey].map(x => x.name)}`,
          });
        } else if (formModelItemKey === 'economicUnit') {
          this.chips.push({
            id: formModelItemKey,
            label: `WE: ${formModel[formModelItemKey]?.id ?? formModel[formModelItemKey]}`,
          });
        } else if (formModel[formModelItemKey]) {
          this.chips.push({
            id: formModelItemKey,
            label: `${field.props.label}: ${formModel[formModelItemKey]}`,
          });
        }
      }
    });
  };

  onTicketTaskSelectionChange() {
    this.sessionStorageService.set(ticketTaskSelectionKey, {
      ticketTaskSelection: this.ticketTaskControl.value,
      ticketViewSelection: this.ticketViewControl.value,
    });
    const filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);
    this.updateFilter({
      ...filterOptions,
      ticketClassList: filterOptions.ticketClassList
        ? filterOptions.ticketClassList.map(x => x.id)
        : null,
      economicUnit: filterOptions.economicUnit?.id,
    });
    this.refreshFromFilter();
  }

  onTicketViewSelectionChange() {
    this.sessionStorageService.set(ticketViewSelectionKey, {
      ticketViewSelection: this.ticketViewControl.value,
    });

    const filterOptions = this.sessionStorageService.get(filterOptionsDtagKey);

    delete filterOptions.ticketClassList;

    this.sessionStorageService.set(filterOptionsDtagKey, filterOptions);

    this.mapFormModelToChips(filterOptions);

    this.updateFilter({
      ...filterOptions,
      ticketClassList: filterOptions.ticketClassList
        ? filterOptions.ticketClassList.map(x => x.id)
        : null,
      economicUnit: filterOptions.economicUnit?.id,
    });

    this.handleTicketTaskOptions();

    if (
      !this.ticketTaskOptions.find(
        option => option.value === this.ticketTaskControl.value
      )
    ) {
      if (
        this.ticketViewControl.value == UserRoles.SMARTPORTAL_DTAG_EK ||
        this.ticketViewControl.value == UserRoles.SMARTPORTAL_REM_FSU
      ) {
        this.sessionStorageService.set(ticketTaskSelectionKey, {
          ticketTaskSelection: 'AllDtagIncidentsWithActiveTasks',
        });

        this.ticketTaskControl.setValue('AllDtagIncidentsWithActiveTasks');
      } else {
        this.sessionStorageService.set(ticketTaskSelectionKey, {
          ticketTaskSelection: 'MyDtagIncidents',
        });

        this.ticketTaskControl.setValue('MyDtagIncidents');
      }
    }

    this.refreshFromFilter();
    this.getData();
  }

  handleTicketTaskOptions() {
    if (this.ticketViewControl.value == UserRoles.SMARTPORTAL_DTAG_USER) {
      this.ticketTaskOptions = this.originalTicketTaskOptions.filter(option =>
        this.userRelatedTicketTaskOptions.some(
          restrictedOption => restrictedOption.value === option.value
        )
      );
    } else if (
      this.ticketViewControl.value == UserRoles.SMARTPORTAL_DTAG_OP_EK
    ) {
      this.ticketTaskOptions = this.oplExtTicketTaskOptions;
    } else if (
      this.ticketViewControl.value == UserRoles.SMARTPORTAL_DTAG_EK ||
      this.ticketViewControl.value == UserRoles.SMARTPORTAL_REM_FSU
    ) {
      this.ticketTaskOptions = this.originalTicketTaskOptions.filter(option =>
        this.restrictedTicketTaskOptions.some(
          restrictedOption => restrictedOption.value === option.value
        )
      );
    } else {
      this.ticketTaskOptions = this.originalTicketTaskOptions;
    }
  }

  exportToExcel() {
    this.canDownload = false;
    this.getTableData$(null, this.config.grid.filter.filterOptions).subscribe({
      next: (res: ResultWrapperOfDtagIncidentTableEntryViewModel) => {
        const data = res.entites;

        const columnMapping = {
          ticketNumber: 'Ticketnummer',
          title: 'Titel',
          ticketClass: 'Ticketklasse',
          product: 'Produkt',
          economicUnit: 'Wirtschaftseinheit',
          postalCode: 'Postleitzahl',
          city: 'Stadt',
          street: 'Straße',
          createdOn: 'Erstellt am',
          status: 'Status',
          rejectionReason: 'Ablehnungsgrund',
          description: 'Beschreibung',
        };

        excelExport(data, columnMapping, 'Tickets', 'Tickets.xlsx');
      },
      error: () => {
        this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  clearTicketViewSelection() {
    this.ticketViewControl.setValue(null);
    this.getData();
  }

  getFilterFields() {
    return [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'ticketNumber',
            type: 'input',
            props: {
              label: this.translate('label__ticketNumber'),
            },
          },
          {
            key: 'name',
            type: 'input',
            props: {
              label: this.translate('label__title'),
            },
          },
          {
            key: 'ticketDescription',
            type: 'input',
            props: {
              label: this.translate('label__incidentDescription'),
            },
          },
          {
            key: 'ticketClassList',
            type: 'select',
            props: {
              label: this.translate('label__incidentClass'),
              options: [],
              multiple: true,
              compareWith: (o1: BaseSelectViewModel, o2: BaseSelectViewModel) =>
                o1?.id === o2?.id,
            },
            hooks: {
              onInit: field => {
                if (field?.props != null) {
                  const f = field.props;

                  const savedFilters =
                    this.sessionStorageService.get(filterOptionsDtagKey);

                  field.formControl.setValue(null);

                  if (
                    this.ticketViewChanged === true ||
                    savedFilters?.ticketClassList?.length > 0
                  ) {
                    f.loading = true;
                    this.loading = true;
                    this.incidentClassSubscription = this.incidentClient
                      .getIncidentClassesByRole(
                        this.ticketViewControl.value ?? ''
                      )
                      .pipe(
                        finalize(() => {
                          f.loading = false;
                          this.loading = false;
                          this.ticketViewChanged = false;
                          this.cd.detectChanges();
                        })
                      )
                      .subscribe({
                        next: (res: BaseSelectViewModel[]) => {
                          f.options = res.map(option => ({
                            label: option.name,
                            value: option,
                          }));

                          if (
                            savedFilters?.ticketClassList &&
                            field.formControl
                          ) {
                            const defaultValue =
                              savedFilters.ticketClassList.map(option => ({
                                id: option.id,
                                name: option.name,
                              }));
                            field.formControl.setValue(defaultValue);

                            const filterOptions =
                              this.sessionStorageService.get(
                                filterOptionsDtagKey
                              );
                            this.config.grid.filter.filterOptions =
                              filterOptions;

                            this.config.grid.filter.filterOptions.economicUnit =
                              this.config.grid.filter.filterOptions.economicUnit?.id;

                            this.mapFormModelToChips(filterOptions);
                          }
                        },
                        error: () => {
                          this.toastr.error(
                            this.translateService.instant(
                              'taskmodule.tickets.list.dtag.error.ticketClass_loading'
                            )
                          );
                        },
                      });
                  }
                }
              },
              onDestroy: _field => {
                if (!this.incidentClassSubscription.closed) {
                  this.incidentClassSubscription.unsubscribe();
                }
              },
            },
          },
          {
            key: 'product',
            type: 'input',
            props: {
              label: this.translate('label__product'),
            },
          },
          ...this.locationFormlyFieldService.autocompleteSearch(
            false,
            this.sessionStorageService.get(filterOptionsDtagKey).economicUnit
              ? {
                  id: this.sessionStorageService.get(filterOptionsDtagKey)
                    .economicUnit,
                }
              : null,
            true,
            null,
            null,
            null,
            this.translateService.instant(
              'location.shared.delete_location_search_tooltip'
            ),
            'economicUnit',
            false,
            this.translateService.instant('location.autocomplete.label')
          ),

          {
            key: 'postalcode',
            type: 'input',
            props: {
              label: this.translate('label__postalCode'),
            },
          },
          {
            key: 'city',
            type: 'input',
            props: {
              label: this.translate('label__incidentCity'),
            },
          },
          {
            key: 'streetName',
            type: 'input',
            props: {
              label: this.translate('label__incidentStreet'),
            },
          },
          {
            key: 'statusreasonList',
            type: 'select',
            props: {
              label: this.translate('label__statusReason'),
              options: [],
              multiple: true,
              loading: true,
              readonly: true,
            },
            hooks: {
              onInit: field => {
                if (field?.props != null) {
                  const f = field.props;

                  if (f.options && (f.options as Array<SysEnum>).length > 0) {
                    return;
                  }
                  f.loading = true;
                  this.optionSetSubscription = this.optionSetClient
                    .getOptionSetByString('Incident_StatusCode')
                    .pipe(first())
                    .subscribe({
                      next: (res: SysEnum[]) => {
                        f.options = res
                          .filter(
                            option =>
                              option.defaultName !==
                                'Bereitgestellte Informationen' &&
                              option.defaultName !== 'technische Dublette'
                          )
                          .map(option => {
                            return {
                              label:
                                this.ibpdiHelperService.getTranslatedEnumLable(
                                  option
                                ),
                              value: option.defaultName,
                            };
                          });
                        f.readonly = false;
                        if (field?.props != null) {
                          f.loading = false;
                        }
                        this.cd.detectChanges();
                      },
                    });
                }
              },
              onDestroy: _field => {
                if (!this.optionSetSubscription.closed) {
                  this.optionSetSubscription.unsubscribe();
                }
              },
            },
          },
          {
            key: 'reajectionReason',
            type: 'input',
            props: {
              label: this.translate('label__rejectionReason'),
            },
          },
          {
            type: 'flex-layout-col',
            fieldGroup: [
              {
                type: 'datepicker',
                key: 'createDateFrom',
                props: {
                  label: this.translate('label__createDateFrom'),
                },
              },
              {
                type: 'datepicker',
                key: 'createDateTo',
                props: {
                  label: this.translate('label__createDateTo'),
                },
              },
            ],
          },
        ],
      },
    ];
  }

  resetLocationSelection() {
    this.locationFormlyFieldService.resetSelection();
    this.dialogData.filterValues =
      this.sessionStorageService.get(filterOptionsDtagKey);
  }

  getFilteredTasks(
    tasks: IncidentTableEntryTaskViewModel[]
  ): IncidentTableEntryTaskViewModel[] {
    if (this.ticketTaskControl.value === 'MyActiveTasks') {
      return tasks.filter(
        task =>
          task.isActive &&
          task.status?.toLowerCase() !== 'abgeschlossen' &&
          task.status?.toLowerCase() !== 'completed'
      );
    }
    return tasks;
  }
}
